import React, { useCallback, useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { mergeClasses, Subtitle1 } from '@fluentui/react-components';
import {
  ClarityTags,
  useClarityTags
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { DataContext } from '@bingads-webui-campaign-react/ads-studio-shared-components';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { TopBanner, TemplateGrid, TemplateDetailsModal } from './components';
import { useDisplayTemplatesPageStyles } from './style';

const names = {
  'Modern minimalist': _TL_('Modern minimalist'),
  'Tech-savvy': _TL_('Tech-savvy'),
  'Vintage, retro': _TL_('Vintage, retro'),
  'Bold and colorful': _TL_('Bold and colorful'),
  'Luxury and beauty': _TL_('Luxury and beauty'),
};

const templateTagOptions = {
  all: _TL_('All'),
  performance: _TL_('Performance'),
  brandAwareness: _TL_('Brand awareness'),
};

const templateAttributesMap = {
  '00000000-0000-0000-0001-000000000001': {
    tag: templateTagOptions.performance,
    assetCounts: {
      logoCount: 1,
      imageCount: 1,
      textAreaCount: 2,
    },
  },
  '00000000-0000-0000-0001-000000000002': {
    tag: templateTagOptions.brandAwareness,
    assetCounts: {
      logoCount: 0,
      imageCount: 1,
      textAreaCount: 2,
    },
  },
  '00000000-0000-0000-0001-000000000003': {
    tag: templateTagOptions.brandAwareness,
    assetCounts: {
      logoCount: 0,
      imageCount: 1,
      textAreaCount: 2,
    },
  },
  '00000000-0000-0000-0001-000000000004': {
    tag: templateTagOptions.brandAwareness,
    assetCounts: {
      logoCount: 0,
      imageCount: 1,
      textAreaCount: 2,
    },
  },
  '00000000-0000-0000-0001-000000000005': {
    tag: templateTagOptions.performance,
    assetCounts: {
      logoCount: 1,
      imageCount: 1,
      textAreaCount: 2,
    },
  },
};

const DisplayTemplatesPage = ({
  i18n,
  logger,
  notificationApi,
  displayTemplatesDataService,
  navigation,
  // permissions,
  // aid,
  // cid,
  // oDataConfig,
  // serverContext,
  // preferenceService,
  pagePerfMarker: displayTemplatePagePerfMarker,
  setDataLoaded,
  // reload,
}) => {
  const classes = useDisplayTemplatesPageStyles();
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplatePreview, setSelectedTemplatePreview] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const activity = useContext(ActivityContext);
  const { setData } = useContext(DataContext);

  const handleFailedToLoadData = useCallback(() => {
    notificationApi.showWarning(i18n.getString(_TL_('Failed to load. Please refresh the page and try again.')));
  }, [i18n, notificationApi]);

  useEffect(() => {
    displayTemplatePagePerfMarker.willFetchData();
    displayTemplatesDataService
      .fetchDisplayAdsTemplateGroups()
      .then(({ data }) => {
        setTemplates(data.displayAdsTemplateGroup);
        data.displayAdsTemplateGroup.forEach((template) => {
          if (template.templateGroupId in templateAttributesMap) {
            templateAttributesMap[template.templateGroupId].templateGroupName = template.templateGroupName in names ?
              i18n.getString(names[template.templateGroupName]) : template.templateGroupName;
          }
        });
      })
      .catch(() => {
        handleFailedToLoadData();
        logger.logTrace('failed to load');
      })
      .finally(() => {
        setIsPageLoading(false);
        displayTemplatePagePerfMarker.willRender();
        setDataLoaded();
      });
  }, []);

  useClarityTags([
    ClarityTags.DisplayTemplateHomePage,
    ClarityTags.LaunchedAdsStudio,
  ]);

  const onViewTemplateDetails = useCallback((templateGroupId) => {
    const templateName = templates.find(template => template.templateGroupId === templateGroupId).templateGroupName;
    setSelectedTemplatePreview({
      templateGroupId,
      templateGroupName: templateName in names ? i18n.getString(names[templateName]) : templateName,
    });

    setOpenModal(true);
  }, [templates, i18n]);

  const onChooseTemplate = useCallback(({ templateGroupDetails, url }) => {
    setData({ templateGroupDetails, url });
    activity.signal(`[DisplayTemplatesPage] templateSelected:${templateGroupDetails.templateGroupId}`);
    if (url) {
      activity.signal(`[DisplayTemplatesPage] url provided: ${url}`);
    } else {
      activity.signal('[DisplayTemplatesPage] url is not provided');
    }
    navigation.gotoDisplayTemplatesCreationPage({
      templateGroupId: templateGroupDetails.templateGroupId,
    });
  }, [navigation, activity, setData]);

  const onCloseModal = useCallback(() => {
    setOpenModal(false);
  }, []);

  return (
    <div className={mergeClasses('ads-studio-display-template-page', classes.displayTemplatePage)}>
      <TopBanner i18n={i18n} />
      <Subtitle1>
        {i18n.getString(_TL_('Get started with a template'))}
      </Subtitle1>
      <TemplateGrid
        loading={isPageLoading}
        templates={templates}
        templateTagOptions={templateTagOptions}
        templateAttributesMap={templateAttributesMap}
        setClickedTemplate={onViewTemplateDetails}
        i18n={i18n}
      />
      {openModal && <TemplateDetailsModal
        fetchDisplayAdsTemplateGroupDetails={displayTemplatesDataService.fetchDisplayAdsTemplateGroupDetails}
        handleFailedToLoadData={handleFailedToLoadData}
        templateGroupId={selectedTemplatePreview.templateGroupId}
        templateGroupName={selectedTemplatePreview.templateGroupName}
        show={openModal}
        onShowChange={setOpenModal}
        onSelectTemplate={onChooseTemplate}
        onClose={onCloseModal}
        i18n={i18n}
        logger={logger}
      />}
    </div>
  );
};

DisplayTemplatesPage.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  displayTemplatesDataService: PropTypes.shape({
    fetchDisplayAdsTemplateGroups: PropTypes.func,
    fetchDisplayAdsTemplateGroupDetails: PropTypes.func,
  }),
  notificationApi: PropTypes.shape({
    showWarning: PropTypes.func,
  }),
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({ }).isRequired,
  adsStudioUserPreferences: PropTypes.shape({}),
  reload: PropTypes.shape({}),
  navigation: PropTypes.shape({
    gotoDisplayTemplatesCreationPage: PropTypes.func,
  }).isRequired,
  perfMarker: PropTypes.shape({}).isRequired,
  oDataConfig: PropTypes.shape({}).isRequired,
  serverContext: PropTypes.shape({}).isRequired,
  preferenceService: PropTypes.shape({}).isRequired,
  instrumentation: PropTypes.shape({
    addScenario: PropTypes.func,
  }).isRequired,
};

DisplayTemplatesPage.defaultProps = {
  notificationApi: {},
  displayTemplatesDataService: {},
  adsStudioUserPreferences: undefined,
  reload: null,
};

export const DisplayTemplatesPageComponent = DisplayTemplatesPage;
